import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MbaIcon } from '../../../assignment/atoms/mba-icon/mba-icon';
import { ui } from '../../../assignment/assignment-ui';
import { ApprovedVideo, FolderLayout } from '../../approved-content.types';
import { downloadPlan, examplePath, formatBytes, totalBytes, videoFileStem } from '../../approved-content.helpers';

const LAYOUTS: { value: FolderLayout; label: string }[] = [
  { value: 'creator', label: 'By creator' },
  { value: 'video', label: 'By video' },
  { value: 'flat', label: 'One folder' },
];

/**
 * "Download …" modal: what to include, how to organise folders, and the MP4 / ZIP summary.
 * Raw files are not offered yet (creators can't upload them). Oversized downloads are
 * blocked with a message until the prepare-and-email flow exists.
 */
@Component({
  selector: 'mbt-download-modal',
  imports: [MbaIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'closed.emit()' },
  template: `
    <div class="fixed inset-0 z-70 grid place-items-center bg-(--mba-scrim) p-4 animate-[mba-fade_220ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none"
      (click)="closed.emit()">
      <div role="dialog" aria-modal="true" aria-labelledby="ac-download-title"
        class="mba flex max-h-[calc(100vh-32px)] w-[min(560px,100%)] flex-col overflow-hidden rounded-2xl bg-(--mba-surface) text-(--fg-1) shadow-[0_20px_48px_rgba(18,6,43,0.14)] animate-[mba-in_220ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none"
        (click)="$event.stopPropagation()">
        <div class="flex items-center gap-3 border-b border-(--border-subtle) px-5 py-4">
          <h2 id="ac-download-title" class="m-0 flex-1 font-(family-name:--font-display) text-[24px] leading-none font-bold">{{ title() }}</h2>
          <button type="button" [class]="ui.iconButton" aria-label="Close" (click)="closed.emit()"><mba-icon name="x" /></button>
        </div>

        <div class="flex flex-col gap-3 overflow-y-auto px-5 py-4">
          <span [class]="ui.kicker">What to include</span>

          <div [class]="optionClass(true)" role="checkbox" aria-checked="true" aria-disabled="true">
            <span [class]="checkClass(true)"><mba-icon name="check" size="size-3.5" [strokeWidth]="3" /></span>
            <span class="flex min-w-0 flex-1 flex-col gap-0.5">
              <b class="font-(family-name:--font-display) text-[17px] leading-none font-bold">Final videos</b>
              <span [class]="ui.sub + ' text-[13px]'">The approved edits, full quality MP4</span>
            </span>
            <span class="text-xs text-(--fg-3) tabular-nums">{{ finalSize() }}</span>
          </div>

          <button type="button" [class]="optionClass(includeSheet())" role="checkbox" [attr.aria-checked]="includeSheet()"
            (click)="includeSheetChange.emit(!includeSheet())">
            <span [class]="checkClass(includeSheet())">
              @if (includeSheet()) { <mba-icon name="check" size="size-3.5" [strokeWidth]="3" /> }
            </span>
            <span class="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
              <b class="font-(family-name:--font-display) text-[17px] leading-none font-bold">Content sheet (PDF)</b>
              <span [class]="ui.sub + ' text-[13px]'">Creator, video and approval date for each video</span>
            </span>
            <span class="text-xs text-(--fg-3) tabular-nums">{{ sheetSize() }}</span>
          </button>

          @if (videos().length > 1) {
          <span [class]="ui.kicker + ' mt-1'">Organise folders</span>
          <div class="inline-flex gap-0.5 self-start rounded-full border border-(--border-subtle) bg-(--bg-alt) p-[3px]" role="group" aria-label="Organise folders">
            @for (option of layouts; track option.value) {
            <button type="button" [class]="segClass(layout() === option.value)" [attr.aria-pressed]="layout() === option.value"
              (click)="layoutChange.emit(option.value)">{{ option.label }}</button>
            }
          </div>
          }
          <div class="overflow-x-auto rounded-lg bg-(--bg-alt) px-3 py-2.5 font-mono text-xs whitespace-nowrap text-(--fg-2)">{{ pathPreview() }}</div>

          <div class="flex items-center gap-3 rounded-lg bg-(--bg-alt) px-3.5 py-3">
            <span class="grid size-9 shrink-0 place-items-center rounded-[10px] bg-(--app-accent-soft) text-(--app-accent-strong)">
              <mba-icon [name]="plan().kind === 'zip' ? 'file-archive' : 'file-video'" />
            </span>
            <span class="flex flex-1 flex-col gap-0.5">
              <b class="text-sm">{{ plan().kind === 'zip' ? 'ZIP' : 'MP4' }} · about {{ planSize() }}</b>
              @if (plan().tooLarge) {
              <span class="text-[13px] font-semibold text-(--danger)">That's more than {{ maxSize() }} in one go. Choose fewer videos and download them in batches.</span>
              } @else {
              <span [class]="ui.sub + ' text-[13px]'">{{ plan().kind === 'zip' ? 'We build the ZIP, then it starts downloading.' : 'Starts straight away.' }}</span>
              }
            </span>
          </div>
        </div>

        <div class="flex items-center justify-end gap-2 border-t border-(--border-subtle) bg-(--bg-alt) px-5 py-3">
          <button type="button" [class]="ui.btnOutline" (click)="closed.emit()">Cancel</button>
          <button type="button" [class]="ui.btnPrimary" [disabled]="plan().tooLarge || busy()" (click)="confirmed.emit()">
            <mba-icon name="download" />{{ busy() ? (plan().kind === 'zip' ? 'Building ZIP…' : 'Starting…') : 'Download' }}
          </button>
        </div>
      </div>
    </div>
  `,
})
export class DownloadModal {
  readonly videos = input.required<readonly ApprovedVideo[]>();
  readonly title = input.required<string>();
  readonly root = input.required<string>();
  readonly includeSheet = input(false);
  readonly layout = input<FolderLayout>('creator');
  readonly sheetBytes = input(0);
  readonly maxBytes = input(Number.MAX_SAFE_INTEGER);
  readonly busy = input(false);

  readonly closed = output<void>();
  readonly confirmed = output<void>();
  readonly includeSheetChange = output<boolean>();
  readonly layoutChange = output<FolderLayout>();

  protected readonly ui = ui;
  protected readonly layouts = LAYOUTS;

  protected readonly plan = computed(() =>
    downloadPlan(this.videos(), this.includeSheet(), this.sheetBytes(), this.maxBytes()),
  );
  protected readonly planSize = computed(() => formatBytes(this.plan().bytes));
  protected readonly finalSize = computed(() => formatBytes(totalBytes(this.videos())));
  protected readonly sheetSize = computed(() => formatBytes(this.sheetBytes()));
  protected readonly maxSize = computed(() => formatBytes(this.maxBytes()));

  protected readonly pathPreview = computed(() => {
    const first = this.videos()[0];
    if (!first) return '';
    if (this.videos().length > 1) return examplePath(this.layout(), this.root(), first);
    return this.plan().kind === 'zip' ? `${this.root()}.zip` : `${videoFileStem(first)}.mp4`;
  });

  protected optionClass(on: boolean): string {
    return `flex w-full cursor-pointer items-center gap-3 rounded-lg border px-3.5 py-3 transition-colors duration-[140ms] ${ui.focusRing} ${
      on ? 'border-(--app-accent) bg-(--mba-row-hi)' : 'border-(--border-subtle) bg-(--mba-surface) hover:border-(--border-strong)'
    }`;
  }

  protected checkClass(on: boolean): string {
    return `grid size-5 shrink-0 place-items-center rounded-[6px] border-[1.5px] text-white ${
      on ? 'border-(--app-accent) bg-(--app-accent)' : 'border-(--border-strong) bg-(--mba-surface)'
    }`;
  }

  protected segClass(on: boolean): string {
    return `inline-flex cursor-pointer items-center gap-1.5 rounded-full px-[13px] py-1.5 text-[13px] font-semibold whitespace-nowrap transition-colors duration-[140ms] ${ui.focusRing} ${
      on ? 'bg-(--mba-surface) text-(--fg-1) shadow-[0_1px_2px_rgba(18,6,43,0.06)]' : 'text-(--fg-2) hover:text-(--fg-1)'
    }`;
  }
}
